'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Stethoscope, UserRound, ClipboardList, Activity, Building2, Phone, MapPin, Mail, Clock, MessageCircle,
  FlaskConical, Navigation, Star, QrCode
} from 'lucide-react';
import TopNav from '@/components/layout/TopNav';
import MedicalVisual from '@/components/ui/MedicalVisual';
import { trackEvent } from '@/lib/analytics';
import { hospitalConfig } from '@/data/hospitalConfig';
import Icon from '@/components/ui/AppIcon';


interface ReceptionOption {
  id: string;
  Icon: React.ElementType;
  label: string;
  sublabel: string;
  href: string;
  isEmergency?: boolean;
  eventSource: string;
  visualKey: string;
}

const receptionOptions: ReceptionOption[] = [
  {
    id: 'option-health-concern',
    Icon: Stethoscope,
    label: 'I HAVE A HEALTH CONCERN',
    sublabel: 'Guide me to the right care',
    href: '/care-journey-screen?start=health-concern',
    eventSource: 'reception-health-concern',
    visualKey: 'health-concern',
  },
  {
    id: 'option-find-doctor',
    Icon: UserRound,
    label: 'I NEED A DOCTOR',
    sublabel: 'Find the right specialist',
    href: '/find-doctor',
    eventSource: 'reception-find-doctor',
    visualKey: 'find-doctor',
  },
  {
    id: 'option-health-checkup',
    Icon: ClipboardList,
    label: 'I WANT A HEALTH CHECK-UP',
    sublabel: 'Find the right wellness package',
    href: '/health-checkup',
    eventSource: 'reception-health-checkup',
    visualKey: 'health-checkup',
  },
  {
    id: 'option-diagnostics',
    Icon: FlaskConical,
    label: 'DIAGNOSTICS',
    sublabel: 'Find the right test or scan',
    href: '/diagnostics',
    eventSource: 'reception-diagnostics',
    visualKey: 'default',
  },
  {
    id: 'option-at-all-is-well',
    Icon: Navigation,
    label: "I'M ALREADY AT ALL IS WELL",
    sublabel: 'Help me find where I need to go',
    href: '/at-all-is-well',
    eventSource: 'reception-at-all-is-well',
    visualKey: 'default',
  },
  {
    id: 'option-memberships',
    Icon: Star,
    label: 'HEALTH MEMBERSHIPS',
    sublabel: 'Explore continuous care programmes',
    href: '/memberships',
    eventSource: 'reception-memberships',
    visualKey: 'default',
  },
  {
    id: 'option-passport',
    Icon: QrCode,
    label: 'DIGITAL HEALTH PASSPORT™',
    sublabel: 'Your secure health identity',
    href: '/health-passport',
    eventSource: 'reception-passport',
    visualKey: 'default',
  },
  {
    id: 'option-follow-up',
    Icon: Activity,
    label: "I'M HERE FOR A FOLLOW-UP",
    sublabel: 'Continue your care journey',
    href: '/appointment-booking-screen',
    eventSource: 'reception-follow-up',
    visualKey: 'follow-up',
  },
  {
    id: 'option-explore',
    Icon: Building2,
    label: 'I WANT TO EXPLORE ALL IS WELL',
    sublabel: 'Learn about our hospital & services',
    href: '#hospital-info',
    eventSource: 'reception-explore',
    visualKey: 'default',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' },
  }),
};

export default function DigitalReceptionContent() {
  const [visible, setVisible] = useState(false);
  const [showHospitalInfo, setShowHospitalInfo] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string>('health-concern');

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const handleOptionClick = (option: ReceptionOption) => {
    if (option.id === 'option-explore') {
      setShowHospitalInfo(true);
      return;
    }
    if (option.isEmergency) {
      trackEvent({ event: 'emergency_clicked', source: option.eventSource });
    } else if (option.id === 'option-health-checkup') {
      trackEvent({ event: 'health_checkup_started', source: option.eventSource });
    }
  };

  return (
    <main
      className="journey-bg min-h-screen flex flex-col pb-20 md:pb-8"
      role="main"
      aria-label="Digital Reception — How can we help you?"
    >
      <TopNav showBackButton backHref="/" backLabel="Home" />

      <div className="flex-1 flex flex-col items-center justify-start pt-24 px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 20 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-6xl"
        >
          {/* Two-column layout */}
          <div className="flex flex-col md:flex-row md:items-start md:gap-12 lg:gap-20">

            {/* LEFT: Options */}
            <div className="flex-1 min-w-0">
              <div className="mb-8">
                <p className="section-label mb-3">ALL IS WELL EXPERIENCE™</p>
                <h1 className="text-display-md text-white font-bold mb-3 leading-tight">
                  HOW CAN WE HELP YOU<br />
                  <span className="gold-gradient-text">TODAY?</span>
                </h1>
                <p className="text-muted-foreground text-sm max-w-md leading-relaxed">
                  Tell us what brings you here and we&apos;ll guide you to the right care.
                </p>
              </div>

              <div className="flex flex-col gap-3" role="list" aria-label="Care options">
                {receptionOptions.map((option, idx) => {
                  const { Icon } = option;
                  const cardClass = `premium-option-card group p-5 flex items-center gap-4 text-left w-full`;

                  if (option.id === 'option-explore') {
                    return (
                      <motion.button
                        key={option.id}
                        custom={idx}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        onClick={() => handleOptionClick(option)}
                        onMouseEnter={() => setHoveredOption(option.visualKey)}
                        whileHover={{ scale: 1.01, y: -2 }}
                        whileTap={{ scale: 0.99 }}
                        className={cardClass}
                        role="listitem"
                        aria-label={option.label}
                      >
                        <div className="option-icon-wrap flex-shrink-0">
                          <Icon size={20} className="text-primary" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="block font-bold text-sm tracking-widest text-foreground uppercase leading-snug">
                            {option.label}
                          </span>
                          <span className="block text-xs text-muted-foreground mt-0.5">{option.sublabel}</span>
                        </div>
                        <span className="text-muted-foreground group-hover:text-primary transition-colors text-sm flex-shrink-0" aria-hidden="true">→</span>
                      </motion.button>
                    );
                  }

                  return (
                    <motion.div
                      key={option.id}
                      custom={idx}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover={{ scale: 1.01, y: -2 }}
                      whileTap={{ scale: 0.99 }}
                      onMouseEnter={() => setHoveredOption(option.visualKey)}
                    >
                      <Link
                        href={option.href}
                        onClick={() => handleOptionClick(option)}
                        className={cardClass}
                        role="listitem"
                        aria-label={option.label}
                      >
                        <div className="option-icon-wrap flex-shrink-0">
                          <Icon size={20} className="text-primary" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="block font-bold text-sm tracking-widest text-foreground uppercase leading-snug">
                            {option.label}
                          </span>
                          <span className="block text-xs text-muted-foreground mt-0.5">{option.sublabel}</span>
                        </div>
                        <span className="text-muted-foreground group-hover:text-primary transition-colors text-sm flex-shrink-0" aria-hidden="true">→</span>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Dynamic visual */}
            <div className="hidden md:flex md:flex-shrink-0 md:w-72 lg:w-80 xl:w-96 items-center justify-center mt-8 md:mt-16">
              <motion.div
                key={hoveredOption}
                initial={{ opacity: 0, scale: 0.85, rotate: -5 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="visual-float"
              >
                <MedicalVisual concern={hoveredOption} className="w-64 h-64 lg:w-72 lg:h-72" />
              </motion.div>
            </div>
          </div>

          {/* Hospital info panel */}
          {showHospitalInfo && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              id="hospital-info"
              className="mt-8 glass-card rounded-2xl p-6"
              role="region"
              aria-label="All Is Well Hospital information"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-lg text-foreground">About All Is Well Hospital</h2>
                <button
                  onClick={() => setShowHospitalInfo(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors text-xl"
                  aria-label="Close hospital information"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-foreground/80">{hospitalConfig.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
                    <a href={hospitalConfig.phoneTel} className="text-primary hover:underline">{hospitalConfig.phone}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
                    <a href={`mailto:${hospitalConfig.email}`} className="text-primary hover:underline">{hospitalConfig.email}</a>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
                    <span className="text-foreground/80">{hospitalConfig.hours.weekdays}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
                    <span className="text-foreground/80">{hospitalConfig.hours.weekends}</span>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <a
                      href={hospitalConfig.maps}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline-gold text-xs px-4 flex items-center gap-1.5"
                      aria-label="Get directions to All Is Well Hospital"
                    >
                      <MapPin size={12} aria-hidden="true" />
                      DIRECTIONS
                    </a>
                    <a
                      href={hospitalConfig.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline-gold text-xs px-4 flex items-center gap-1.5"
                      aria-label="WhatsApp All Is Well Hospital"
                    >
                      <MessageCircle size={12} aria-hidden="true" />
                      WHATSAPP
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </main>
  );
}